import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useFormik } from 'formik';
import { sorts } from '../../utils';
import Layout from '../../pages/Layout';
import { useSelector } from 'react-redux';
import AdminProviderSelect from '../../components/AdminProviderSelect';
import { DownloadReportButton, Terms, getLabel } from './ReportsUtils';
import { lastDayOfQuarter, parse } from 'date-fns';

export const getRoyaltyByVendorId = ({ id, ...params }) => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/reports/${id}`,
            params,
            method: 'GET'
        })
        .then((resp) => ({ id, data: resp.data }));
};

export const getVendors = () => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/vendors`,
            params: { page_size: 1000 },
            method: 'GET'
        })
        .then((resp) => resp.data);
};

const SalesReports = () => {
    const session = useSelector((state) => state.session);
    const [vendors, setVendors] = useState(session?.user?.vendors || []);
    const [results, setResults] = useState([]);
    const { values, setFieldValue } = useFormik({
        initialValues: {
            selectedProvider: session?.user?.provider || {}
        },
        enableReinitialize: true
    });

    const getReports = useCallback(async (ids = []) => {
        setResults(ids.map((id) => ({ id: id, loading: true })));
        ids.forEach((id) => {
            getRoyaltyByVendorId({ id })
                .then((resp) => {
                    resp.loading = false;
                    setResults((currentResults) =>
                        currentResults.map((report) => {
                            if (report.id === resp.id) return resp;
                            return report;
                        })
                    );
                })
                .catch((err) => console.error(err));
        });
    }, []);

    useEffect(() => {
        let vendorArray = [session.selectedVendor] || [];
        getReports(vendorArray);
    }, [session.selectedVendor, getReports]);

    useEffect(() => {
        if (session.isAdmin) {
            getVendors().then(({ results }) => setVendors(results));
        }
    }, [session.isAdmin]);

    const handleProviderChange = (id, val) => {
        setFieldValue('selectedProvider', val);
        getReports(val.vendors.map((vendor) => vendor.id));
    };

    const { user, isAdmin } = session;
    const userVendors = isAdmin ? vendors : user?.vendors;

    const getReportTitle = (report) => {
        if (!report) return '';

        const {
            report_type,
            report_definition: { report_type_name, period_name },
            source
        } = report;

        if (report_type === 'FINANCIAL' && source !== 'spotify') {
            return `${getLabel(report)} ${report_type_name}`;
        } else if (report_type === 'FINANCIAL' && source === 'spotify') {
            return `${report_type_name}`;
        }

        if (report_type_name === 'Standard Royalty') {
            return `${period_name} Digital Royalty`;
        } else {
            return `${period_name} ${report_type_name}`;
        }
    };

    const getReportDate = (report) => {
        const date = parse(report.report_definition.period_start_date);
        if (report?.term?.toLowerCase() === Terms.QUARTERLY) {
            return lastDayOfQuarter(date);
        }
        return date;
    };

    return (
        <Layout>
            <div className="page-header">
                <h1>Royalty Reports</h1>
                <div className="flash fixed mb-4 flash-outline-primary sales-flash">
                    <p>
                        This is your official royalty reporting. For unofficial real-time sales reports please visit{' '}
                        <Link to={'/reporting/sales'}>
                            <b>Preliminary Real-Time Sales</b>
                        </Link>
                    </p>
                </div>
                <p>
                    Your royalty statements will appear here as they become available according to your current
                    reporting schedule.
                </p>
            </div>

            <div className="row">
                <div className="col-xs-12 col-xl-9">
                    <AdminProviderSelect
                        id="selectedProvider"
                        label="View Reports for:"
                        onChange={handleProviderChange}
                        value={values?.selectedProvider?.id}
                    />

                    {!!(userVendors || []).length &&
                        results
                            .sort((a, b) => sorts.asc(a.id, b.id))
                            .map(({ id, data, loading }) => {
                                let vend = (userVendors || []).filter((v) => v).find((v) => v.id === parseInt(id));
                                if (!vend) return null;

                                let name = [
                                    ...new Set((data?.results || []).map((v) => v?.report_definition?.vendor_name))
                                ];

                                return (
                                    <React.Fragment key={vend.id}>
                                        {!isAdmin && <h6>{vend.name}</h6>}
                                        {isAdmin && name && <h6>{name.join(', ')}</h6>}

                                        <ul className="list-group mb-6">
                                            {loading && <li className="list-group-item">Loading...</li>}
                                            {!loading && !data.results?.length && (
                                                <li className="list-group-item">
                                                    Check back here to find your next royalty statement.
                                                </li>
                                            )}
                                            {!loading &&
                                                data.results?.length &&
                                                data.results
                                                    .filter(
                                                        (report) =>
                                                            !report.report_type || report.report_type === 'FINANCIAL'
                                                    )
                                                    .sort((a, b) => sorts.desc(getReportDate(a), getReportDate(b)))
                                                    .map((report) => (
                                                        <li key={report.id} className="list-group-item mb-2">
                                                            <span>{getReportTitle(report)}</span>
                                                            <span className="txt-right">
                                                                {report.report_files.map((file) => (
                                                                    <DownloadReportButton
                                                                        key={`${report.id}-${file.file_type}`}
                                                                        report={file}
                                                                        label={file.file_type}
                                                                        className="ml-2"
                                                                    />
                                                                ))}
                                                            </span>
                                                        </li>
                                                    ))}
                                        </ul>
                                    </React.Fragment>
                                );
                            })}
                </div>
            </div>
        </Layout>
    );
};

export default SalesReports;

